import { el, mount, onTap } from '../ui/dom.ts';
import { iconEl } from '../ui/icons.ts';
import { navigate } from '../router.ts';
import { leaveShell } from '../ui/shell.ts';
import { planSection, type PlansPayload } from './plans-section.ts';
import { api } from '../api.ts';
import { skeletonPlans } from '../ui/feedback.ts';
import { BRAND, brandMark } from '../ui/brand.ts';
import { t } from '../i18n/index.ts';
import { legalContent } from '../i18n/legal.ts';
import { langSwitch } from '../ui/lang-switch.ts';

// Landing — `/`, oturum gerektirmez ve otomatik yönlendirme yapmaz.
// Bölüm sırası 10-TASARIM-V2'den: nav → başlık → ürün vitrini → cihaz satırı →
// IPTV anlatımı → fiyatlar → SSS → footer.
//
// Vitrindeki illüstrasyonlar özgün demo materyalidir (docs/design-v2/assets).
// Gerçek katalog iddiası taşımazlar; altyazıları bunu söyler.

const DEMO = {
  coast: '/demo/coast.svg',
  mountain: '/demo/mountain.svg',
  city: '/demo/city.svg',
  dunes: '/demo/dunes.svg',
};

export async function landingView(): Promise<void> {
  leaveShell();
  document.body.classList.add('on-paper');
  const app = document.getElementById('app')!;

  const cta = (label: string, target: string, klass = 'btn btn-dark') => {
    const button = el('button', { class: klass, type: 'button' }, label);
    onTap(button, () => navigate(target));
    return button;
  };

  const goTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const anchor = (label: string, id: string, extraClass = '') => {
    const button = el('button', { class: extraClass, type: 'button' }, label);
    onTap(button, () => goTo(id));
    return button;
  };

  // Landing oturumdan bağımsızdır: giriş yapmış kullanıcı da aynı sayfayı
  // görür (kişisel paket/hesap bilgisi yok). "Giriş yap" oturumluyken
  // router koruması sayesinde doğrudan uygulamaya götürür.
  const primaryLabel = t('landing.cta.startFree');
  const primaryTarget = '/login?mode=signup';

  const page = el('div', { class: 'landing' },
    // 1 — Navigasyon
    el('header', { class: 'wrap landing-nav' },
      brandButton(),
      el('nav', { class: 'navlinks' },
        anchor(t('landing.nav.experience'), 'deneyim', 'desktop-only'),
        anchor(t('landing.nav.plans'), 'paketler', 'desktop-only'),
        langSwitch(),
        cta(t('landing.nav.login'), '/login', 'nav-text'),
      ),
    ),

    // 2 — Başlık alanı
    el('section', { class: 'wrap' },
      el('div', { class: 'intro' },
        el('h1', {}, t('landing.hero.title1'), el('br'), t('landing.hero.title2')),
        el('div', { class: 'intro-side' },
          el('p', {}, t('landing.hero.lead')),
          primaryButton(primaryLabel, primaryTarget),
          el('span', { class: 'note' }, t('landing.hero.noteFree')),
        ),
      ),

      // 3 — Ürün vitrini
      el('div', { class: 'showcase' },
        el('div', { class: 'showcase-nav' },
          el('span', { class: 'mark' }, brandMark(18), el('span', {}, BRAND.name)),
          el('span', { class: 'on' }, t('nav.discover')),
          el('span', {}, t('nav.live')),
          el('span', {}, t('nav.mylist')),
          el('span', { class: 'hide-sm' }, t('nav.iptv')),
          el('span', { class: 'right' }, t('landing.showcase.sample')),
        ),
        el('div', { class: 'scene' },
          el('img', { src: DEMO.coast, alt: t('landing.scene.alt') }),
          el('div', { class: 'scene-copy' },
            el('span', { class: 'tag' }, t('landing.scene.tag')),
            el('h2', {}, t('landing.scene.title1'), el('br'), t('landing.scene.title2')),
            el('p', {}, t('landing.scene.body1'), el('br'), t('landing.scene.body2')),
            el('div', { class: 'scene-actions' }, lightButton(t('landing.scene.cta'), primaryTarget)),
          ),
        ),
        el('div', { class: 'showcase-shelf' },
          miniContinue(DEMO.mountain, t('landing.shelf.1'), t('landing.shelf.meta', { n: 18 }), 62),
          miniContinue(DEMO.city, t('landing.shelf.2'), t('landing.shelf.meta', { n: 12 }), 40),
          miniContinue(DEMO.dunes, t('landing.shelf.3'), t('landing.shelf.meta', { n: 24 }), 25),
        ),
      ),
      el('div', { class: 'caption' },
        el('span', {}, t('landing.caption.1')),
        el('span', {}, t('landing.caption.2')),
      ),

    ),

    // 4 — Ne izlenir: üç kaynak. Sayfa eskiden ürünün NE yaptığını söylemeden
    // doğrudan IPTV anlatımına geçiyordu.
    el('section', { class: 'wrap pillars-section' },
      el('h2', { class: 'section-title' }, t('landing.pillars.title1'), el('br'), t('landing.pillars.title2')),
      el('div', { class: 'pillars' },
        pillar('compass', t('landing.pillar.1.title'), t('landing.pillar.1.body')),
        pillar('broadcast', t('landing.pillar.2.title'), t('landing.pillar.2.body')),
        pillar('list', t('landing.pillar.3.title'), t('landing.pillar.3.body'), 'Plus'),
      ),
    ),

    // 5 — Araç ekranı: ürünü ayıran şey buydu ama tek satırlık bir cihaz
    // listesinde "Araç" sözcüğünden ibaretti.
    el('section', { class: 'wrap' },
      el('div', { class: 'car-band' },
        el('div', { class: 'car-copy' },
          el('span', { class: 'tag' }, t('landing.car.tag')),
          el('h2', { class: 'section-title' }, t('landing.car.title1'), el('br'), t('landing.car.title2')),
          el('p', {}, t('landing.car.body')),
          el('p', { class: 'car-safety' }, t('landing.car.safety')),
        ),
        el('ul', { class: 'car-points' },
          el('li', {}, iconEl('check'), el('span', {}, t('landing.car.point1'))),
          el('li', {}, iconEl('check'), el('span', {}, t('landing.car.point2'))),
          el('li', {}, iconEl('check'), el('span', {}, t('landing.car.point3'))),
        ),
      ),
      el('div', { class: 'device-line' },
        el('span', { class: 'lead' }, t('landing.car.devices')),
        el('span', {}, t('landing.device.phone')),
        el('span', {}, t('landing.device.tablet')), el('span', {}, t('landing.device.pc')),
        el('span', {}, t('landing.device.tv')),
        uyumlulukButton(),
      ),
    ),

    // 5 — IPTV anlatımı
    el('section', { class: 'wrap', id: 'deneyim' },
      el('div', { class: 'feature' },
        el('div', {},
          el('h2', { class: 'section-title' }, t('landing.feature.title1'), el('br'), t('landing.feature.title2')),
          el('p', {}, t('landing.feature.body')),
          anchorLink(t('landing.feature.link'), 'paketler'),
        ),
        el('div', { class: 'feature-demo' },
          el('div', { class: 'source-window' },
            el('div', { class: 'source-head' },
              el('span', {}, t('landing.source.head')),
              el('span', { class: 'tag' }, 'Plus'),
            ),
            el('h3', {}, t('landing.source.title')),
            el('p', {}, t('landing.source.body')),
            sourceRow('list', t('landing.source.row1'), t('landing.source.row1meta')),
            sourceRow('film', t('landing.source.row2'), t('landing.source.row2meta')),
            sourceRow('star', t('landing.source.row3'), t('landing.source.row3meta')),
          ),
        ),
      ),
    ),

    // 6 — Fiyatlar (uygulamadaki Paketler ekranıyla aynı bileşen)
    el('section', { class: 'wrap pricing', id: 'paketler' }, el('div', { id: 'landing-plans' }, skeletonPlans())),

    // 7 — SSS
    el('section', { class: 'wrap faq' },
      el('h2', { class: 'section-title' }, t('landing.faq.title1'), el('br'), t('landing.faq.title2')),
      el('div', {},
        faqItem(t('landing.faq.q1'), t('landing.faq.a1')),
        faqItem(t('landing.faq.q2'), t('landing.faq.a2')),
        faqItem(t('landing.faq.q3'), t('landing.faq.a3')),
        faqItem(t('landing.faq.q4'), t('landing.faq.a4')),
        faqItem(t('landing.faq.q5'), t('landing.faq.a5')),
        faqItem(t('landing.faq.q6'), t('landing.faq.a6')),
      ),
    ),

    // Kapanış çağrısı: sayfa SSS'den doğrudan alt bilgiye iniyordu.
    el('section', { class: 'wrap' },
      el('div', { class: 'closing' },
        el('div', {},
          el('h2', { class: 'section-title' }, t('landing.closing.title')),
          el('p', {}, t('landing.closing.body')),
        ),
        primaryButton(primaryLabel, primaryTarget),
      ),
    ),

    // 8 — Footer: yasal bölümlerin tamamına bağlantı (verify:publish bunu denetler)
    el('footer', { class: 'wrap landing-footer' },
      brandButton(),
      el('div', { class: 'legal' },
        ...legalContent().sections.map((section) => legalButton(section.label, section.key)),
      ),
      el('div', { class: 'footer-end' },
        el('span', {}, t('landing.footer.tagline')),
        langSwitch(),
      ),
    ),
  );

  mount(app, page);
  window.scrollTo(0, 0);

  // Fiyatlar gerçek API'den gelir; landing'de de uygulamadaki değerlerin aynısı.
  try {
    const data = await api.get<PlansPayload>('/api/billing/plans');
    mount(page.querySelector('#landing-plans') as HTMLElement, planSection(data, { context: 'landing' }));
  } catch {
    mount(page.querySelector('#landing-plans') as HTMLElement,
      el('p', { class: 'fine' }, t('landing.plans.loadFail')));
  }
}

function brandButton(): HTMLElement {
  const button = el('button', { class: 'wordmark', type: 'button', 'aria-label': `${BRAND.title} — ${t('nav.home')}` },
    brandMark(26), el('span', {}, BRAND.name));
  onTap(button, () => navigate('/'));
  return button;
}

function primaryButton(label: string, target: string): HTMLElement {
  const button = el('button', { class: 'btn btn-dark', type: 'button' }, label, iconEl('arrowUpRight'));
  onTap(button, () => navigate(target));
  return button;
}

function lightButton(label: string, target: string): HTMLElement {
  const button = el('button', { class: 'btn btn-light', type: 'button' }, iconEl('play'), label);
  onTap(button, () => navigate(target));
  return button;
}

function anchorLink(label: string, id: string): HTMLElement {
  const button = el('button', { class: 'link-underline', type: 'button' }, label);
  onTap(button, () => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  return button;
}

/** Uyumluluk bağlantısı gerçek test matrisine gider; rozet/garanti değildir. */
function uyumlulukButton(): HTMLElement {
  const button = el('button', { type: 'button' }, t('landing.device.compat'));
  onTap(button, () => navigate('/compatibility'));
  return button;
}

function legalButton(label: string, section: string): HTMLElement {
  const button = el('button', { type: 'button' }, label);
  onTap(button, () => navigate(`/legal?section=${section}`));
  return button;
}

function miniContinue(src: string, title: string, meta: string, percent: number): HTMLElement {
  return el('div', { class: 'mini-continue' },
    el('div', { class: 'thumb' },
      el('img', { src, alt: '' }),
      el('i', { style: { width: `${percent}%` } }),
    ),
    el('div', {}, el('b', {}, title), el('p', {}, meta)),
  );
}

function pillar(iconName: 'compass' | 'broadcast' | 'list', title: string, body: string, tag?: string): HTMLElement {
  return el('article', { class: 'pillar' },
    el('span', { class: 'ico' }, iconEl(iconName)),
    el('h3', {}, title, tag ? el('span', { class: 'tag' }, tag) : null),
    el('p', {}, body),
  );
}

function sourceRow(iconName: 'list' | 'film' | 'star', label: string, meta: string): HTMLElement {
  return el('div', { class: 'source-row' },
    el('span', { class: 'ico' }, iconEl(iconName)),
    el('span', {}, label),
    el('small', {}, meta),
  );
}

function faqItem(question: string, answer: string): HTMLElement {
  return el('details', {},
    el('summary', {}, question),
    el('p', {}, answer),
  );
}
